import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Diagnostic page — dumps everything the React app "knows" about the
 * current user. No auth guard, no styling, plain readable output.
 *
 * Visit /diag while logged in to compare what the frontend sees vs.
 * what curl /api/auth/me returns. Any discrepancy = the frontend has
 * a stale state or the axios call is hitting a different endpoint.
 */
export default function Diag() {
  const [me, setMe] = useState({ state: "loading" });
  const [billing, setBilling] = useState({ state: "loading" });
  const [trial, setTrial] = useState({ state: "loading" });
  const [health, setHealth] = useState({ state: "loading" });

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setMe({ state: "ok", data }))
      .catch((e) => setMe({ state: "err", err: e?.response?.status || e?.message }));

    api.get("/billing/status")
      .then(({ data }) => setBilling({ state: "ok", data }))
      .catch((e) => setBilling({ state: "err", err: e?.response?.status || e?.message }));

    api.get("/auth/trial-status")
      .then(({ data }) => setTrial({ state: "ok", data }))
      .catch((e) => setTrial({ state: "err", err: e?.response?.status || e?.message }));

    api.get("/system/health")
      .then(({ data }) => setHealth({ state: "ok", data }))
      .catch((e) => setHealth({ state: "err", err: e?.response?.status || e?.message }));
  }, []);

  const box = { padding: "14px", margin: "10px 0", border: "2px solid #444", background: "#fafafa", color: "#111", fontFamily: "monospace", fontSize: "12px", whiteSpace: "pre-wrap", wordBreak: "break-word" };
  const label = { fontWeight: "bold", fontSize: "14px", padding: "6px 0", color: "#000" };

  const meData = me.data || {};
  const summary = {
    __env: process.env.REACT_APP_BACKEND_URL,
    __location: typeof window !== "undefined" ? window.location.href : "",
    __cookie_present: typeof document !== "undefined" ? document.cookie.includes("session_token") : "n/a",
    __me_is_admin_field: meData.is_admin,
    __me_email: meData.email,
    __hasFullAccess_would_be: (billing.data?.is_pro === true) || (meData.is_admin === true),
  };

  return (
    <div style={{ padding: 20, background: "#fff", color: "#000", minHeight: "100vh" }}>
      <h1 style={{ margin: "0 0 10px", color: "#000" }}>Frontend Diagnostic</h1>
      <p style={{ color: "#555", fontSize: 13 }}>
        This is the exact data the React app has at this moment. Compare with
        <code> /api/auth/me </code> in a plain browser tab. Any mismatch = the
        frontend axios call isn&#39;t hitting the same endpoint or isn&#39;t
        sending the cookie.
      </p>

      <div style={label}>SUMMARY</div>
      <pre style={{ ...box, background: "#fffbe6", borderColor: "#e0a930" }}>
        {JSON.stringify(summary, null, 2)}
      </pre>

      <div style={label}>GET /api/auth/me</div>
      <pre style={box}>{JSON.stringify(me, null, 2)}</pre>

      <div style={label}>GET /api/billing/status</div>
      <pre style={box}>{JSON.stringify(billing, null, 2)}</pre>

      <div style={label}>GET /api/auth/trial-status</div>
      <pre style={box}>{JSON.stringify(trial, null, 2)}</pre>

      <div style={label}>GET /api/system/health</div>
      <pre style={box}>{JSON.stringify(health, null, 2)}</pre>
    </div>
  );
}
